// Libraries
import React from 'react';

// Supermove
import {Icon, IconSource, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  UrlFiltersType,
  useInlineFormMutation,
  usePopover,
  useResponsive,
  useState,
} from '@supermove/hooks';
import {Project, ProjectModel} from '@supermove/models';
import {Typography, colors} from '@supermove/styles';
import {Datetime} from '@supermove/utils';

// App
import Callout from '@shared/design/components/Callout';
import TextTooltip from '@shared/design/components/TextTooltip';
import EditFieldTextAreaButton from '@shared/modules/App/components/EditFieldTextAreaButton';
import ProjectStatusBadge from '@shared/modules/Project/components/ProjectStatusBadge';
import SkeletonLoader from 'modules/App/components/SkeletonLoader';
import ProjectHeaderActions from 'modules/Project/V2/Show/components/ProjectHeaderActions';
import ProjectHeaderClientInformation from 'modules/Project/V2/Show/components/ProjectHeaderClientInformation';
import ProjectTags from 'modules/Project/V2/Show/components/ProjectTags';
import ProjectType from 'modules/Project/V2/Show/components/ProjectType';
import {ProjectPageUrlFiltersType} from 'modules/Project/V2/types/ProjectPageUrlFilters';

const ProjectHeaderContainer = Styled.View<{isCancelled?: boolean}>`
  padding-horizontal: 24px;
  padding-vertical: 12px;
  background-color: ${({isCancelled}) => (isCancelled ? colors.gray.background : colors.white)};
`;

const Column = Styled.View`
`;

const Row = Styled.View`
  flex-direction: row;
`;

const ProjectName = Styled.Text`
  ${Typography.Heading1}
`;

const IconContainer = Styled.View`
`;

const MicroText = Styled.Text`
  ${Typography.Micro}
`;

const ExpandCollapseButton = Styled.ButtonV2`
  flex-shrink: 1;
`;

const VerticalLine = Styled.View`
  height: 100%;
  width: 1px;
  background-color: ${colors.gray.border};
`;

const getCancellationReasonText = ({project}: {project: ProjectModel}) => {
  const {projectCancellationReason} = project;

  if (!project.cancelledAt) {
    return 'Cancelled.';
  }

  const cancelledAt = Datetime.convertToDisplayDatetime(project.cancelledAt);
  if (projectCancellationReason) {
    return (
      `Cancelled at ${cancelledAt} due to ` +
      `${projectCancellationReason.name}` +
      `${projectCancellationReason.details ? `: ${projectCancellationReason.details}` : '.'}`
    );
  }
  return `Cancelled at ${cancelledAt}.`;
};

const LoadingComponent = () => {
  return (
    <ProjectHeaderContainer>
      <Row>
        <Column style={{flex: 1}}>
          <SkeletonLoader height={SkeletonLoader.HEIGHT.Heading1Text} width={360} />
          <Space height={12} />
          <Row>
            <SkeletonLoader height={SkeletonLoader.HEIGHT.MicroText} width={120} />
            <Space width={32} />
            <SkeletonLoader height={SkeletonLoader.HEIGHT.MicroText} width={120} />
            <Space width={32} />
            <SkeletonLoader height={SkeletonLoader.HEIGHT.MicroText} width={120} />
          </Row>
        </Column>
        <Column>
          <SkeletonLoader height={SkeletonLoader.HEIGHT.MicroText} width={120} />
          <Space height={8} />
          <SkeletonLoader height={SkeletonLoader.HEIGHT.MicroText} width={120} />
          <Space height={8} />
          <SkeletonLoader height={SkeletonLoader.HEIGHT.MicroText} width={120} />
        </Column>
      </Row>
    </ProjectHeaderContainer>
  );
};

const ProjectDetail = ({
  iconSource,
  text,
  tooltipText,
}: {
  iconSource: IconSource;
  text: string;
  tooltipText?: string;
}) => {
  return (
    <TooltipWrapper tooltipText={tooltipText}>
      <Row style={{alignItems: 'center'}}>
        <IconContainer>
          <Icon source={iconSource} size={Icon.Sizes.Small} color={colors.gray.secondary} />
        </IconContainer>
        <Space width={8} />
        <MicroText>{text}</MicroText>
      </Row>
    </TooltipWrapper>
  );
};

const TooltipWrapper = ({
  tooltipText,
  children,
}: {
  tooltipText?: string;
  children: React.ReactNode;
}) => {
  if (tooltipText) {
    return (
      <TextTooltip isEnabledMobileToast={false} text={tooltipText}>
        {children}
      </TextTooltip>
    );
  }
  return <React.Fragment>{children}</React.Fragment>;
};

const CollapsibleDescription = ({project}: {project: ProjectModel}) => {
  const text = project.description;
  const responsive = useResponsive();
  const popover = usePopover();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const {ref, isHovered} = EditFieldTextAreaButton.useHover();
  const {form, submitting, handleSubmit} = useInlineFormMutation({
    name: 'projectDescriptionForm',
    enableReinitialize: true,
    form: {
      projectId: project.id,
      description: project.description,
    },
    mutation: gql`
      mutation projectHeaderUpdateProjectDescription($projectDescriptionForm: ProjectDescriptionForm!) {
        response: updateProjectDescription(projectDescriptionForm: $projectDescriptionForm) {
          ${gql.errors}
          project {
            id
            description
          }
        }
      }
    `,
    onSuccess: popover.handleClose,
    onError: (errors) => {
      console.log({errors});
    },
  });

  return (
    <Row style={{flex: 1}} ref={ref}>
      <ExpandCollapseButton
        onPress={() =>
          text ? setIsDescriptionExpanded(!isDescriptionExpanded) : popover.handleToggle()
        }
      >
        <MicroText numberOfLines={isDescriptionExpanded ? undefined : 1}>
          {text || 'No description'}
        </MicroText>
      </ExpandCollapseButton>
      <EditFieldTextAreaButton
        form={form}
        field={'projectDescriptionForm.description'}
        handleSubmit={handleSubmit}
        isSubmitting={submitting}
        placeholder={'Enter description'}
        popover={popover}
        buttonLeftOffset={text.length < 20 ? 0 : 340}
        drawerHeaderText={'Edit Description'}
        isHidden={responsive.desktop && !isHovered}
        style={{paddingLeft: 4, marginTop: -4, zIndex: 1000}}
      />
      <Space width={24} />
    </Row>
  );
};

const SpaceWithLine = ({width = 12}) => (
  <Row>
    <Space width={width} />
    <VerticalLine />
    <Space width={width} />
  </Row>
);

const ProjectHeader = ({
  project,
  urlFilters,
  refetchAndReset,
}: {
  project: ProjectModel;
  urlFilters: UrlFiltersType<ProjectPageUrlFiltersType>;
  refetchAndReset: () => void;
}) => {
  return (
    <ProjectHeaderContainer isCancelled={project.isCancelled}>
      {project.isCancelled && (
        <React.Fragment>
          <Space height={12} />
          <Callout text={getCancellationReasonText({project})} />
          <Space height={24} />
        </React.Fragment>
      )}
      <Row>
        <Column style={{flex: 1}}>
          <Row style={{alignItems: 'center'}}>
            <Row>
              <ProjectStatusBadge project={project} />
              <Space width={8} />
            </Row>
            <ProjectName>{Project.getName(project)}</ProjectName>
            <Space width={4} />
            <ProjectHeaderActions project={project} refetch={refetchAndReset} />
          </Row>
          <Space height={12} />
          <Row style={{flexWrap: 'wrap'}}>
            <ProjectDetail text={project.organization.name} iconSource={Icon.Building} />
            <SpaceWithLine />
            <ProjectType projectType={project.projectType} />
            <SpaceWithLine />
            <ProjectDetail
              text={Project.getDate(project)}
              iconSource={Icon.Calendar}
              tooltipText={'Start & End Date'}
            />
            <SpaceWithLine />
            {!!project.size && (
              <React.Fragment>
                <ProjectDetail
                  text={project.size}
                  iconSource={Icon.HouseBuilding}
                  tooltipText={'Project Size'}
                />
                <SpaceWithLine />
              </React.Fragment>
            )}
            <ProjectDetail text={`${project.weight} lbs`} iconSource={Icon.WeightHanging} />
            <SpaceWithLine />
            <ProjectDetail text={`${project.volume} ft³`} iconSource={Icon.RulerTriangle} />
            <SpaceWithLine />
            <ProjectDetail
              text={Project.getLocation(project)}
              iconSource={Icon.MapPin}
              tooltipText={'Origin & Destination'}
            />
          </Row>
          <Space height={12} />
          <CollapsibleDescription project={project} />
          <Space height={10} />
          <ProjectTags project={project} refetchAndReset={refetchAndReset} />
        </Column>
        <ProjectHeaderClientInformation project={project} urlFilters={urlFilters} />
      </Row>
    </ProjectHeaderContainer>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectHeader.listener = gql`
  ${Project.getLocation.fragment}
  fragment ProjectHeader_listener on Project {
    id
    weight
    ...Project_getLocation
  }
`;

ProjectHeader.fragment = gql`
  ${Project.getDate.fragment}
  ${Project.getLocation.fragment}
  ${Project.getName.fragment}
  ${ProjectHeaderActions.fragment}
  ${ProjectHeaderClientInformation.fragment}
  ${ProjectHeader.listener}
  ${ProjectStatusBadge.fragment}
  ${ProjectType.fragment}
  ${ProjectTags.fragment}

  fragment ProjectHeader on Project {
    id
    uuid
    description
    weight
    volume
    size
    isCancelled
    cancelledAt
    organization {
      id
      name
    }
    projectCancellationReason {
      id
      name
      details
    }
    ...Project_getDate
    ...Project_getLocation
    ...Project_getName
    ...ProjectHeaderActions
    ...ProjectHeaderClientInformation
    ...ProjectHeader_listener
    ...ProjectStatusBadge
    ...ProjectType
    ...ProjectTags
  }
`;

ProjectHeader.SkeletonLoader = LoadingComponent;

export default ProjectHeader;
